import React, { useCallback, useEffect, useState } from 'react';
import {
  IconButton, Badge, Tooltip, Menu, Box, Typography, Divider, List, ListItemButton,
  ListItemText, Button, CircularProgress,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useNavigate } from 'react-router-dom';
import { notificationApi } from '../services/employeeService';
import { tokens } from '../theme';
import { countLabel, formatDateTime } from '../utils/format';

interface Notification {
  id: string;
  type: string;
  title: string;
  body?: string;
  data?: unknown;
  isRead: boolean;
  createdAt: string;
}

const TYPE_LABEL: Record<string, string> = {
  SHIFT_NOT_CLOSED: 'Niezamknięta zmiana',
  ABSENCE_REPORTED: 'Zgłoszenie nieobecności',
  ADJUSTMENT_PENDING: 'Wniosek o korektę',
  PAYROLL_PENDING_APPROVAL: 'Rozliczenie do zatwierdzenia',
  MANUAL_TIME_CHANGE: 'Ręczna zmiana czasu',
  PROFITABILITY_BELOW_THRESHOLD: 'Niska rentowność',
  COST_RATIO_EXCEEDED: 'Przekroczony koszt',
  GENERIC: 'Powiadomienie',
  // Tasks module
  TASK_ASSIGNED: 'Nowe zadanie',
  TASK_DUE_CHANGED: 'Zmiana terminu',
  TASK_COMMENT: 'Komentarz do zadania',
  TASK_MENTION: 'Wspomniano o Tobie',
  TASK_DUE_SOON: 'Zbliża się termin',
  TASK_OVERDUE: 'Przekroczony termin',
  TASK_SUBMITTED_REVIEW: 'Zadanie do weryfikacji',
  TASK_SUBMITTED_APPROVAL: 'Zadanie do akceptacji',
  TASK_APPROVED: 'Zadanie zaakceptowane',
  TASK_REWORK: 'Zwrot do poprawy',
  TASK_STATUS_CHANGED: 'Zmiana statusu zadania',
  // Bookings
  BOOKING_FORCED: 'Wizyta mimo ostrzeżeń',
};

/** Task notifications carry the task id, so the bell can navigate straight to it. */
const taskIdOf = (n: { data?: unknown }): string | undefined => {
  const data = n.data as { taskId?: string } | undefined;
  return data?.taskId;
};

/**
 * Powiadomienia o wizytach niosa jej identyfikator i date. Kalendarz nie ma
 * trasy do pojedynczej wizyty, wiec otwieramy go na wlasciwym dniu i mowimy,
 * ktory kafel podswietlic.
 */
const bookingTargetOf = (n: { data?: unknown }): string | undefined => {
  const data = n.data as { bookingId?: string; date?: string } | undefined;
  if (!data?.bookingId) return undefined;
  const params = new URLSearchParams({ bookingId: data.bookingId });
  if (data.date) params.set('date', data.date);
  return `/salon/calendar?${params.toString()}`;
};

export const NotificationsMenu: React.FC = () => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadCount = useCallback(async () => {
    try {
      const r = await notificationApi.unreadCount();
      setUnread(r.count);
    } catch {
      /* not authorized / network — keep silent */
    }
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 60000); // poll every 60s
    return () => clearInterval(t);
  }, [loadCount]);

  const open = async (e: React.MouseEvent<HTMLElement>) => {
    setAnchor(e.currentTarget);
    setLoading(true);
    try {
      setItems((await notificationApi.list()) as Notification[]);
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (id: string) => {
    await notificationApi.markRead(id);
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    loadCount();
  };

  const markAll = async () => {
    await notificationApi.markAllRead();
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnread(0);
  };

  const unreadInList = items.filter((n) => !n.isRead).length;

  return (
    <>
      <Tooltip title="Powiadomienia">
        <IconButton onClick={open} aria-label={unread ? `Powiadomienia, nieprzeczytane: ${unread}` : 'Powiadomienia'} aria-haspopup="menu">
          <Badge
            badgeContent={unread}
            color="error"
            max={99}
            sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem', height: 18, minWidth: 18, px: 0.5, border: `2px solid ${tokens.paper}`, boxSizing: 'content-box' } }}
          >
            <NotificationsNoneIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { sx: { mt: 1, width: 380, maxWidth: 'calc(100vw - 24px)', maxHeight: 520, display: 'flex', flexDirection: 'column' } } }}
        MenuListProps={{ sx: { p: 0 } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.4 }}>Powiadomienia</Typography>
            {!loading && items.length > 0 && (
              <Typography variant="caption" component="div">
                {unreadInList ? `${countLabel(unreadInList, 'nieprzeczytane', 'nieprzeczytane', 'nieprzeczytanych')}` : 'Wszystkie przeczytane'}
              </Typography>
            )}
          </Box>
          {unreadInList > 0 && (
            <Button size="small" onClick={markAll} sx={{ flexShrink: 0 }}>Oznacz jako przeczytane</Button>
          )}
        </Box>
        <Divider sx={{ my: '0 !important' }} />
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
        ) : items.length === 0 ? (
          <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
            <Box sx={{ width: 44, height: 44, mx: 'auto', mb: 1.25, borderRadius: '50%', bgcolor: tokens.plumSoft, color: tokens.plum, display: 'grid', placeItems: 'center' }}>
              <NotificationsNoneIcon sx={{ fontSize: 22 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>Nie masz powiadomień</Typography>
            <Typography variant="body2" sx={{ color: tokens.inkSoft, mt: 0.5 }}>
              Tu pojawią się informacje o zadaniach, wizytach i czasie pracy.
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ overflowY: 'auto', py: 0.5 }}>
            {items.map((n) => (
              <ListItemButton
                key={n.id}
                onClick={() => {
                  if (!n.isRead) markRead(n.id);
                  const taskId = taskIdOf(n);
                  if (taskId) {
                    setAnchor(null);
                    navigate(`/tasks/${taskId}`);
                    return;
                  }
                  const bookingTarget = bookingTargetOf(n);
                  if (bookingTarget) {
                    setAnchor(null);
                    navigate(bookingTarget);
                  }
                }}
                sx={{
                  alignItems: 'flex-start',
                  gap: 1.25,
                  mx: 0.75,
                  my: 0.25,
                  px: 1.25,
                  py: 1,
                  bgcolor: n.isRead ? 'transparent' : tokens.plumSofter,
                  '&:hover': { bgcolor: n.isRead ? tokens.surfaceStrong : tokens.plumSoft },
                }}
              >
                <Box
                  aria-hidden
                  sx={{ width: 8, height: 8, mt: '8px', borderRadius: '50%', flexShrink: 0, bgcolor: n.isRead ? 'transparent' : tokens.plum }}
                />
                <ListItemText
                  sx={{ my: 0 }}
                  primary={n.title}
                  secondary={(
                    <>
                      {n.body && (
                        <Box component="span" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', color: tokens.inkSoft, mt: 0.25 }}>
                          {n.body}
                        </Box>
                      )}
                      <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
                        {TYPE_LABEL[n.type] || 'Powiadomienie'} · {formatDateTime(n.createdAt)}
                      </Box>
                    </>
                  )}
                  primaryTypographyProps={{ fontSize: '0.9375rem', fontWeight: n.isRead ? 500 : 700, color: n.isRead ? tokens.inkSoft : tokens.ink, lineHeight: 1.4 }}
                  secondaryTypographyProps={{ fontSize: '0.8125rem', color: tokens.muted, component: 'span', display: 'block' }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Menu>
    </>
  );
};

export default NotificationsMenu;
